import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form", "name", "lname", "email", "phone", "address",
    "success", "sendButton",
    "formContainer", "thanksContainer", "thanksHeading"
  ]

  static values = {
    action: String,
    tailwind: { type: Boolean, default: false },
    successMessage: { type: String, default: "Thanks for your message!" },
    errorMessage: { type: String, default: "Sorry that didn't work... Please try again." },
    hideForm: { type: Boolean, default: true }
  }

  connect() {
    if (!this.hasFormTarget) return

    this.formTarget.addEventListener("submit", this.handleSubmit.bind(this))

    if (this.hasNameTarget) {
      this.nameTarget.addEventListener("focus", this.clearMessages.bind(this))
    }
  }

  handleSubmit(event) {
    event.preventDefault()

    if (!this.validateForm()) {
      return
    }

    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true
    }

    const formData = new FormData(this.formTarget)

    let firstName = this.nameTarget.value
    if (firstName.indexOf(' ') >= 0) {
      firstName = firstName.split(' ').slice(0, -1).join(' ')
    }

    fetch(this.actionValue || this.formTarget.action || '/contact.js', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.json()
    })
    .then(data => {
      this.showSuccess()
      this.formTarget.reset()
    })
    .catch(error => {
      console.error('Error submitting form:', error)
      this.showError()
      this.formTarget.reset()
    })
    .finally(() => {
      setTimeout(() => {
        if (this.hasSendButtonTarget) {
          this.sendButtonTarget.disabled = false
        }
      }, 1000)
    })
  }

  validateForm() {
    let isValid = true

    const requiredTargets = [
      { target: this.nameTarget, name: "name" },
      { target: this.emailTarget, name: "email" },
      { target: this.addressTarget, name: "message" }
    ]

    requiredTargets.forEach(({ target, name }) => {
      if (!target.value.trim()) {
        this.addError(target, `${name.charAt(0).toUpperCase() + name.slice(1)} is required`)
        isValid = false
      } else {
        this.removeError(target)
      }
    })

    if (this.hasEmailTarget && this.emailTarget.value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(this.emailTarget.value)) {
        this.addError(this.emailTarget, "Please enter a valid email address")
        isValid = false
      }
    }

    return isValid
  }

  addError(element, message) {
    if (this.tailwindValue) {
      const wrapper = element.closest("div")
      if (wrapper) {
        element.classList.add("border-red-500")
        element.classList.remove("border-gray-300")
        let errorEl = wrapper.querySelector("[data-error]")
        if (!errorEl) {
          errorEl = document.createElement("p")
          errorEl.setAttribute("data-error", "")
          errorEl.className = "mt-1 text-sm text-red-600"
          wrapper.appendChild(errorEl)
        }
        errorEl.textContent = message
      }
    } else {
      const controlGroup = element.closest(".form-group") || element.closest(".control-group")
      if (controlGroup) {
        controlGroup.classList.add("has-error")
        let helpBlock = controlGroup.querySelector(".help-block")
        if (!helpBlock) {
          helpBlock = document.createElement("div")
          helpBlock.className = "help-block"
          const controls = controlGroup.querySelector(".controls") || controlGroup
          controls.appendChild(helpBlock)
        }
        helpBlock.textContent = message
      }
    }
  }

  removeError(element) {
    if (this.tailwindValue) {
      const wrapper = element.closest("div")
      if (wrapper) {
        element.classList.remove("border-red-500")
        element.classList.add("border-gray-300")
        const errorEl = wrapper.querySelector("[data-error]")
        if (errorEl) errorEl.remove()
      }
    } else {
      const controlGroup = element.closest(".form-group") || element.closest(".control-group")
      if (controlGroup) {
        controlGroup.classList.remove("has-error")
        const helpBlock = controlGroup.querySelector(".help-block")
        if (helpBlock) helpBlock.textContent = ""
      }
    }
  }

  clearMessages() {
    if (this.hasSuccessTarget) {
      this.successTarget.innerHTML = ""
    }
  }

  showSuccess() {
    if (this.hideFormValue && this.hasFormContainerTarget && this.hasThanksContainerTarget) {
      this.formContainerTarget.hidden = true
      this.thanksContainerTarget.hidden = false

      if (this.hasThanksHeadingTarget) {
        this.thanksHeadingTarget.focus({ preventScroll: true })
      } else if (typeof this.thanksContainerTarget.focus === "function") {
        this.thanksContainerTarget.focus({ preventScroll: true })
      }

      if (typeof this.thanksContainerTarget.scrollIntoView === "function") {
        this.thanksContainerTarget.scrollIntoView({ behavior: "smooth", block: "start" })
      }
      return
    }

    if (!this.hasSuccessTarget) return

    const message = this.successMessageValue
    if (this.tailwindValue) {
      this.successTarget.innerHTML = `
        <div class="rounded-lg bg-green-50 border border-green-200 p-4 flex items-start justify-between">
          <p class="text-sm font-medium text-green-800"></p>
          <button type="button" data-action="click->contact-form#clearMessages" class="ml-4 text-green-500 hover:text-green-700">&times;</button>
        </div>
      `
      this.successTarget.querySelector("p").textContent = message
    } else {
      this.successTarget.innerHTML = `
        <div class="alert alert-success">
          <button type="button" class="close" data-dismiss="alert" aria-hidden="true">&times;</button>
          <strong></strong>
        </div>
      `
      this.successTarget.querySelector("strong").textContent = message
      const closeButton = this.successTarget.querySelector(".close")
      if (closeButton) {
        closeButton.addEventListener("click", () => { this.successTarget.innerHTML = "" })
      }
    }
  }

  showError() {
    if (!this.hasSuccessTarget) return

    const message = this.errorMessageValue
    if (this.tailwindValue) {
      this.successTarget.innerHTML = `
        <div class="rounded-lg bg-red-50 border border-red-200 p-4 flex items-start justify-between">
          <p class="text-sm font-medium text-red-800"></p>
          <button type="button" data-action="click->contact-form#clearMessages" class="ml-4 text-red-500 hover:text-red-700">&times;</button>
        </div>
      `
      this.successTarget.querySelector("p").textContent = message
    } else {
      this.successTarget.innerHTML = `
        <div class="alert alert-danger">
          <button type="button" class="close" data-dismiss="alert" aria-hidden="true">&times;</button>
          <strong></strong>
        </div>
      `
      this.successTarget.querySelector("strong").textContent = message
      const closeButton = this.successTarget.querySelector(".close")
      if (closeButton) {
        closeButton.addEventListener("click", () => { this.successTarget.innerHTML = "" })
      }
    }
  }
}
